/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * Trends › The Bridge and receivers (docs/CONTRACT-fleet-governance.md sections 7.5, 7.6, 9.1 and 12.3): per receiver its
 * calls, failures, refusals, contract refusals, p95 and the envelope rules that fired, with a sparkline; the Bridge audit
 * refusals by HTTP status; and The Lab filings per app. These are counted from the turn traces, the receivers' own counts
 * and the Bridge audit emitter, which write no audit rows; only the service's own Lab filings (`pulse`) have ledger rows.
 * The health state of each receiver is Pulse's: "Health in Pulse" goes there.
 */
import { Radio } from 'lucide-react';
import { countRows, trendsApi, trendsExportHref, type GovLabRow, type GovReceiverRow } from '../../lib/trends';
import { EmptyState, Panel, Sparkline, StatusChip, Table, Tag, type Column } from '../../ui';
import { count } from '../../ui/format';
import { useLoad } from '../pulse/common';
import { ExportLink, PageLink } from '../fleet/parts';
import { SectionError, ShowRows, useClientSort, type SectionProps } from './parts';

const msText = (ms: number | null) => (ms === null ? '—' : ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s`);

export function TrendsReceivers({ query, filter, onShowRows }: SectionProps) {
  const { load, reload, reloading } = useLoad(() => trendsApi.receivers(query), `receivers|${JSON.stringify(query)}`);
  const answer = load.state === 'ready' ? load.data : null;
  const receivers = useClientSort(answer?.items ?? [], (row, key) => (key === 'receiver' ? row.receiver : key === 'p95Ms' ? row.p95Ms : (row[key as 'calls' | 'failed' | 'refused' | 'contract'] ?? 0)), ['receiver']);
  const bridge = countRows(answer?.bridgeAudit).map((row) => ({ status: row.key, count: row.count }));
  const lab = useClientSort(answer?.lab ?? [], (row, key) => (key === 'app' ? row.app : row[key as 'filed' | 'refused']), ['app']);

  const receiverColumns: Array<Column<GovReceiverRow>> = [
    { key: 'receiver', header: 'Receiver', sortable: true, cell: (row) => <Tag>{row.receiver}</Tag> },
    { key: 'calls', header: 'Calls', numeric: true, sortable: true, cell: (row) => count(row.calls) },
    { key: 'failed', header: 'Failed', numeric: true, sortable: true, cell: (row) => count(row.failed) },
    { key: 'refused', header: 'Refused', numeric: true, sortable: true, cell: (row) => count(row.refused) },
    { key: 'contract', header: 'Contract', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => count(row.contract) },
    { key: 'p95Ms', header: 'p95', numeric: true, sortable: true, hideOnPhone: true, cell: (row) => msText(row.p95Ms) },
    {
      key: 'rules', header: 'Rules', hideOnPhone: true,
      cell: (row) => (countRows(row.rules).length ? <span className="ui-chips">{countRows(row.rules).map((rule) => <StatusChip key={rule.key} tone="neutral" plain>{`${rule.key} ${count(rule.count)}`}</StatusChip>)}</span> : <span className="ui-quiet">—</span>),
    },
    { key: 'spark', header: 'Refusals per day', hideOnPhone: true, cell: (row) => <Sparkline values={row.spark} label={`${row.receiver}: refusals per day`} tone={row.refused + row.contract > 0 ? 'warn' : 'data'} /> },
  ];
  const bridgeColumns: Array<Column<{ status: string; count: number }>> = [
    { key: 'status', header: 'HTTP status', mono: true, cell: (row) => row.status },
    { key: 'count', header: 'Refusals', numeric: true, cell: (row) => count(row.count) },
  ];
  const labColumns: Array<Column<GovLabRow>> = [
    { key: 'app', header: 'App', sortable: true, cell: (row) => <Tag>{row.app}</Tag> },
    { key: 'filed', header: 'Filed', numeric: true, sortable: true, cell: (row) => count(row.filed) },
    { key: 'refused', header: 'Refused', numeric: true, sortable: true, cell: (row) => count(row.refused) },
    {
      key: 'rows', header: <span className="ui-sr">Audit rows</span>, align: 'end',
      cell: (row) => (row.app === 'pulse' && answer ? (
        <ShowRows onClick={() => onShowRows({ title: 'The Lab · filings by the service', choices: [{ label: 'Filed', event: 'pulse.lab_filed' }, { label: 'Refused', event: 'pulse.lab_refused' }], range: { from: answer.range.from, to: answer.range.to } })} />
      ) : null),
    },
  ];

  return (
    <Panel
      flush
      title="The Bridge and receivers"
      description="Calls to each receiving app, what they refused and why, and the Bridge's audit refusals"
      actions={(
        <span className="ui-toolbar">
          <PageLink href="/pulse-tools">Health in Pulse</PageLink>
          <ExportLink href={trendsExportHref('receivers', filter)} />
        </span>
      )}
    >
      {load.state === 'failed' ? <div className="ui-panel-pad"><SectionError title="The receivers could not be loaded" error={load.error} onRetry={reload} retrying={reloading} /></div> : (
        <>
          <Table
            label="Receivers"
            columns={receiverColumns}
            rows={receivers.rows}
            rowKey={(row) => row.receiver}
            loading={load.state === 'loading'}
            skeletonRows={3}
            sort={receivers.sort}
            empty={<EmptyState compact icon={Radio} title="No receiver calls in this range">Calls to the receiving apps appear here.</EmptyState>}
          />
          <div className="ui-panel-pad ui-panel-pad--divided">
            <div className="ui-section">
              <h3 className="ui-section-title">Bridge audit refusals</h3>
              <Table label="Bridge audit refusals" columns={bridgeColumns} rows={bridge} rowKey={(row) => row.status} loading={load.state === 'loading'} skeletonRows={2} stickyHeader={false} empty={<p className="ui-para ui-quiet">The Bridge refused no audit event in this range.</p>} />
            </div>
            <div className="ui-section">
              <h3 className="ui-section-title">The Lab filings</h3>
              <Table label="The Lab filings" columns={labColumns} rows={lab.rows} rowKey={(row) => row.app} loading={load.state === 'loading'} skeletonRows={2} stickyHeader={false} sort={lab.sort} empty={<p className="ui-para ui-quiet">No bug reports were filed to The Lab in this range.</p>} />
            </div>
          </div>
        </>
      )}
    </Panel>
  );
}
